import * as React from 'react';
import useId from '@mui/utils/useId';

import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { FileListItem } from 'src/components/FileListItem';
import { Stack, type StackProps } from 'src/components/Stack';

import type { FileModel } from '../fileModel';
import type { FileUploaderProps } from '../FileUploader';

/**
 * @access package
 */
export const defaultMaxFileListItemsToDisplay = 3;

type FileUploaderFileListOwnProps = {
  /**
   * @access package
   */
  fileValues: FileModel[];

  /**
   * @access package
   */
  actionFooterClassName: string;

  /**
   * @access package
   */
  onFileChange: FileUploaderProps['onChange'];
};

type FileUploaderFileListProps = React.HTMLAttributes<HTMLElement> &
  Omit<StackProps, 'onChange'> &
  Pick<
    FileUploaderProps,
    | 'maxFileListItemsToDisplay'
    | 'maxFileListContainerHeight'
    | 'onRemoveFileListItem'
    | 'renderFileListItem'
    | 'renderFileListItemActionMenuItems'
    | 'showFileSize'
    | 'slotProps'
  > &
  FileUploaderFileListOwnProps;

export function FileUploaderFileList(
  props: FileUploaderFileListProps
): React.ReactElement | null {
  const {
    id: idProp,
    fileValues: fileValuesProp,
    renderFileListItem: renderFileListItemProp,
    renderFileListItemActionMenuItems: renderFileListItemActionMenuItems,
    maxFileListItemsToDisplay:
      maxFileListItemsToDisplayProp = defaultMaxFileListItemsToDisplay,
    maxFileListContainerHeight,
    showFileSize,
    onRemoveFileListItem,
    onFileChange,
    slotProps,
    actionFooterClassName,
    ...rest
  } = props;

  const { actionFooterButtons, ...fileListContainerProps } =
    slotProps?.fileList ?? {};

  const listId = useId(idProp);
  const alwaysDisplayAllFileListItems = maxFileListItemsToDisplayProp === -1;
  const [numberOfFileListItemsToDisplay, setNumberOfFileListItemsToDisplay] =
    React.useState<number | 'all'>(
      alwaysDisplayAllFileListItems ? 'all' : maxFileListItemsToDisplayProp
    );

  React.useEffect(() => {
    setNumberOfFileListItemsToDisplay(
      alwaysDisplayAllFileListItems ? 'all' : maxFileListItemsToDisplayProp
    );
  }, [alwaysDisplayAllFileListItems, maxFileListItemsToDisplayProp]);

  const toggleNumberOfFileListItemsToDisplay =
    React.useCallback<React.MouseEventHandler>(() => {
      setNumberOfFileListItemsToDisplay(
        numberOfFileListItemsToDisplay === 'all'
          ? maxFileListItemsToDisplayProp
          : 'all'
      );
    }, [maxFileListItemsToDisplayProp, numberOfFileListItemsToDisplay]);

  function renderFileListItem(
    fileModel: FileModel,
    key: string
  ): React.ReactElement | null {
    const item = renderFileListItemProp ? (
      renderFileListItemProp(fileModel)
    ) : (
      <FileListItem
        role="listitem"
        {...fileModel}
        renderFileListItemActionMenuItems={renderFileListItemActionMenuItems}
        showFileSize={showFileSize}
        onRemove={onRemoveFileListItem}
      />
    );

    // Wrap in a fragment so we don't have to worry about key-ing custom elements returned from consumer's renderFileListItemProp
    return <React.Fragment key={key}>{item}</React.Fragment>;
  }

  if (fileValuesProp.length === 0) return null;

  const filesToDisplay =
    numberOfFileListItemsToDisplay === 'all'
      ? fileValuesProp
      : fileValuesProp.slice(0, numberOfFileListItemsToDisplay);

  function getButtonText(): string {
    if (maxFileListItemsToDisplayProp === 0) {
      return numberOfFileListItemsToDisplay === 'all'
        ? 'Hide files'
        : 'Show files';
    }

    return `Show ${numberOfFileListItemsToDisplay === 'all' ? 'less' : 'more'}`;
  }

  const shouldShowActionFooter =
    !alwaysDisplayAllFileListItems &&
    fileValuesProp.length > maxFileListItemsToDisplayProp;

  return (
    <Box
      component="aside"
      aria-labelledby={listId}
      sx={{
        ...(maxFileListContainerHeight && {
          overflowY: 'auto',
          maxHeight: maxFileListContainerHeight,
          // Ensure that at least one FileListItem is visible.
          minHeight: 50,
        }),
      }}
    >
      <Stack
        aria-label={`${fileValuesProp.length} uploaded ${
          fileValuesProp.length === 1 ? 'file' : 'files'
        }`}
        spacing={1}
        role="list"
        sx={{
          pb:
            shouldShowActionFooter ||
            actionFooterButtons?.showClearAllButton === true
              ? '8px'
              : 0,
        }}
        {...fileListContainerProps}
        {...rest}
        id={listId}
      >
        {mapFileModelsKeyed(filesToDisplay, renderFileListItem)}
      </Stack>
      {(shouldShowActionFooter ||
        actionFooterButtons?.showClearAllButton === true) && (
        // TODO (adl): intl this label / button text
        <Stack direction={'row'} spacing={1} className={actionFooterClassName}>
          {shouldShowActionFooter &&
            actionFooterButtons?.showMoreLessButton !== false && (
              <Button
                aria-controls={listId}
                aria-label={`Display ${
                  numberOfFileListItemsToDisplay === 'all'
                    ? maxFileListItemsToDisplayProp
                    : 'all'
                } files`}
                variant={
                  actionFooterButtons?.showMoreLessButtonVariant ?? 'outlined'
                }
                sx={{ mt: 1, alignSelf: 'start' }}
                onClick={toggleNumberOfFileListItemsToDisplay}
              >
                {getButtonText()}
              </Button>
            )}
          {actionFooterButtons?.showClearAllButton !== false && (
            <Button
              aria-controls={listId}
              aria-label={`Clear all files`}
              variant={actionFooterButtons?.clearAllButtonVariant ?? 'text'}
              sx={{ mt: 1 }}
              onClick={() => onFileChange([], [])}
            >
              {`Clear all`}
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
}

/**
 * Like {@link Array.map}, but also passes a unique, mostly-stable React key for each file.
 *
 * Keys will be stable across renders for most cases, avoiding remounting/rerendering where possible.
 *
 * The main edge-case is if multiple similar files (in name and size) are reordered relative to each other,
 * then they may remount.
 */
function mapFileModelsKeyed<T>(
  fileModels: FileModel[],
  mapper: (fileModel: FileModel, key: string) => T
): T[] {
  // This local variable ensures there aren't key collisions between similar files,
  // and should be reset each time we iterate through the file list
  // (and not persisted across renders, such as with a ref).
  const countsByKeyPrefix: Record<string, number | undefined> = {};

  return fileModels.map((fileModel) => {
    // Try to build as unique and stable of a key as possible from the file itself.
    const keyPrefix = `${fileModel.file.name}-${fileModel.file.size}`;
    // Check for collisions (which could occur for similar files, or duplicates of the same file),
    // incrementing a counter to ensure each file has its own unique key.
    const count = (countsByKeyPrefix[keyPrefix] =
      (countsByKeyPrefix[keyPrefix] ?? 0) + 1);
    const key = `${keyPrefix}.${count}`;

    return mapper(fileModel, key);
  });
}

export default FileUploaderFileList;
